import React, {useId} from 'react';

import {MenuItem} from '@pb/kit-component-menu';
import {withCssModule} from '@pb/kit-util-with-css-module';

import styles from './styles.module.styl';

import type {MenuItemProps} from '@pb/kit-component-menu';
import type {ComponentPropsWithoutRef, ReactNode} from 'react';


const classNames = withCssModule(styles);

type NativeProps = ComponentPropsWithoutRef<'div'>;

export interface DropdownMenuGroupProps extends NativeProps {
    /**
     * Group header caption. Rendered as a non-clickable label above the grouped items.
     */
    caption: ReactNode,
    /**
     * Props forwarded to the caption `MenuItem` element (e.g. `separated`, `iconBefore`, `className`, etc.).
     */
    captionProps?: Omit<MenuItemProps, 'children'>,
}

/**
 * DropdownMenuGroup
 *
 * Groups related `DropdownMenuItem` elements under a non-interactive caption/header.
 *
 * @example
 * ```tsx
 * <DropdownMenu>
 *     <DropdownMenuGroup caption="Group 1">
 *         <DropdownMenuItem caption="Item 1" />
 *         <DropdownMenuItem caption="Item 2" />
 *     </DropdownMenuGroup>
 *     <DropdownMenuGroup caption="Group 2">
 *         <DropdownMenuItem caption="Item 3" />
 *     </DropdownMenuGroup>
 * </DropdownMenu>
 * ```
 *
 * @see [Storybook]{@link https://storybook.dev.pitchbookdata.com/?path=/docs/hover-dropdown-menu--documentation}
 */
export function DropdownMenuGroup(props: DropdownMenuGroupProps) {
    const {caption, children, captionProps, ...nativeProps} = props;
    const id = useId();

    return (
        <div
            role="group"
            aria-labelledby={id}
            {...nativeProps}
            className={classNames('dropdown-menu-group', nativeProps.className)}>
            <MenuItem
                {...captionProps}
                id={id}
                className={classNames('dropdown-menu-group-caption', captionProps?.className)}>
                {caption}
            </MenuItem>
            {children}
        </div>
    );
}

DropdownMenuGroup.displayName = 'DropdownMenuGroup';
